import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MessageCircle, MapPin, Instagram, Check } from "lucide-react";
import axios from "axios";
import { Reveal } from "../components/Reveal";
import { CONTACT } from "../data/products";
import Seo from "../components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const empty = { name: "", company: "", email: "", phone: "", product: "", quantity: "", message: "" };

const fields = [
  { name: "name", label: "Full Name", required: true, type: "text", half: true },
  { name: "company", label: "Company", type: "text", half: true },
  { name: "email", label: "Email", required: true, type: "email", half: true },
  { name: "phone", label: "Phone", type: "tel", half: true },
  { name: "product", label: "Product / Requirement", type: "text", half: true },
  { name: "quantity", label: "Estimated Quantity", type: "text", half: true },
];

export default function Contact() {
  const [form, setForm] = useState(empty);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [hp, setHp] = useState("");
  const startRef = useRef(Date.now());
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { setError("Please provide your name and email."); return; }
    setStatus("loading"); setError("");
    try {
      await axios.post(`${API}/quotes`, { ...form, website: hp, elapsed_ms: Date.now() - startRef.current });
      setStatus("success"); setForm(empty);
    } catch {
      setStatus("idle"); setError("Something went wrong. Please try again.");
    }
  };

  const methods = [
    { icon: Phone, label: "Phone", value: CONTACT.phone, href: `tel:${CONTACT.phoneHref}` },
    { icon: Mail, label: "Email", value: CONTACT.email, href: `mailto:${CONTACT.email}` },
    { icon: MessageCircle, label: "WhatsApp", value: CONTACT.whatsapp, href: `https://wa.me/${CONTACT.whatsappHref}` },
    { icon: Instagram, label: "Instagram", value: CONTACT.instagramHandle, href: CONTACT.instagram },
    { icon: MapPin, label: "Address", value: CONTACT.address.join(", ") },
  ];

  return (
    <div data-testid="contact-page" className="pt-32">
      <Seo
        title="Contact & Request a Quote | Clinixport Global"
        description="Tell us what you need. Request a quote from Clinixport Global for medical supplies, PPE and protective hygiene products, or reach us by phone, email or WhatsApp."
        path="/contact"
        keywords="request a quote, medical supplies quote, PPE supplier contact, Clinixport Global contact"
      />
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 pb-16">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#1F6FEB] mb-5">Contact · Request a Quote</p>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tighter text-[#0A1128] leading-[1.02] max-w-4xl">
            Let's talk about what you need.
          </h1>
        </Reveal>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 pb-28 grid grid-cols-1 lg:grid-cols-12 gap-12">
        {/* FORM */}
        <div className="lg:col-span-7">
          <div className="bg-white border border-[#0A1128]/10 p-8 sm:p-10">
            {status === "success" ? (
              <div className="flex flex-col items-center text-center py-16" data-testid="contact-success">
                <div className="w-16 h-16 bg-[#0D9488] flex items-center justify-center mb-6"><Check size={32} className="text-white" /></div>
                <h3 className="text-2xl font-extrabold tracking-tight text-[#0A1128]">Enquiry received</h3>
                <p className="mt-3 text-[#475569] max-w-sm leading-relaxed">Thank you. Our supply team will review your requirement and respond shortly.</p>
                <button onClick={() => setStatus("idle")} className="mt-8 border border-[#0A1128] px-8 py-3.5 font-bold text-xs tracking-widest uppercase hover:bg-[#0A1128] hover:text-white transition-all">Send another</button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="contact-form" className="space-y-5">
                <input
                  type="text" name="website" value={hp} onChange={(e) => setHp(e.target.value)}
                  tabIndex={-1} autoComplete="off" aria-hidden="true"
                  style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {fields.map((f) => (
                    <div key={f.name}>
                      <label className="block text-xs font-bold uppercase tracking-wider text-[#64748B] mb-2">
                        {f.label}{f.required && <span className="text-[#1F6FEB]"> *</span>}
                      </label>
                      <input type={f.type} value={form[f.name]} onChange={set(f.name)} data-testid={`contact-${f.name}`}
                        className="w-full bg-white border border-[#0A1128]/20 px-4 py-3 text-[#0A1128] outline-none focus:border-[#1F6FEB] transition-colors" />
                    </div>
                  ))}
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-[#64748B] mb-2">Message</label>
                  <textarea rows={5} value={form.message} onChange={set("message")} data-testid="contact-message"
                    className="w-full bg-white border border-[#0A1128]/20 px-4 py-3 text-[#0A1128] outline-none focus:border-[#1F6FEB] transition-colors resize-none" />
                </div>
                {error && <p className="text-sm text-red-600" data-testid="contact-error">{error}</p>}
                <button type="submit" disabled={status === "loading"} data-testid="contact-submit"
                  className="w-full bg-[#1F6FEB] text-white hover:bg-[#1556B8] transition-colors px-8 py-4 font-bold text-sm tracking-widest uppercase disabled:opacity-60">
                  {status === "loading" ? "Sending…" : "Request a Quote"}
                </button>
              </form>
            )}
          </div>
        </div>

        {/* CONTACT METHODS */}
        <div className="lg:col-span-5">
          <div className="bg-[#0A1128] text-white p-8 sm:p-10 h-full">
            <h2 className="text-2xl font-extrabold tracking-tight">Direct contact</h2>
            <p className="mt-3 text-white/60 leading-relaxed">Prefer to reach us directly? Use any of the channels below.</p>
            <div className="mt-8 space-y-6">
              {methods.map((m) => {
                const Inner = (
                  <>
                    <div className="w-11 h-11 shrink-0 border border-white/15 flex items-center justify-center text-[#38bdf8] group-hover:border-[#38bdf8] transition-colors"><m.icon size={18} /></div>
                    <div>
                      <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/50">{m.label}</p>
                      <p className="mt-1 font-semibold group-hover:text-[#38bdf8] transition-colors">{m.value}</p>
                    </div>
                  </>
                );
                return m.href ? (
                  <a key={m.label} href={m.href} target={m.href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer"
                     className="group flex items-start gap-4" data-testid={`contact-method-${m.label.toLowerCase()}`}>{Inner}</a>
                ) : (
                  <div key={m.label} className="group flex items-start gap-4" data-testid={`contact-method-${m.label.toLowerCase()}`}>{Inner}</div>
                );
              })}
            </div>
            {CONTACT.placeholder && (
              <p className="mt-8 text-xs text-white/30 leading-relaxed border-t border-white/10 pt-6">
                Note: contact details shown are placeholders and will be replaced with confirmed company information.
              </p>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
